import DataGrid from 'devextreme/ui/data_grid';
import { AppInjector } from '@core/injector/injector.module';
import { DeviceDetectorService } from 'ngx-device-detector';
import { RC } from '@m4u-angular/core/framework/resources.service';
import { SysGlobals } from '@m4u-angular/core/framework/sysglobals';
import { EnumLanguages } from '@m4u-angular/core/framework/sysenums.enum';
import { FrameworkService } from '@m4u-angular/core/framework/framework.service';
import { ScreenService } from '@m4u-angular/core/framework/screen.service';
import { LocalStorageService } from '@m4u-angular/core/framework/local-storage.service';

export function SetGridDefaults() {
    const Detector = AppInjector.get(DeviceDetectorService);
    const localStorageService = AppInjector.get(LocalStorageService);

    DataGrid.defaultOptions
        ({
            options:
            {
                columnResizingMode: 'nextColumn',
                allowColumnResizing: true,
                allowColumnReordering: true,
                columnHidingEnabled: true,
                columnAutoWidth: true,
                dateSerializationFormat: 'yyyy-MM-dd',
                hoverStateEnabled: true,
                showBorders: true,
                showRowLines: true,
                renderAsync: true,

                stateStoring:
                {
                    enabled: true,
                    type: 'custom',
                    savingTimeout: 1000,
                    storageKey: '', 
                    keepSorting: false,

                    customSave(state) {
                        state.selectionFilter = state.selectedRowKeys = state.searchText = undefined;
                        if (state.columns)
                            for (const column of state.columns) {
                                delete column.filterType;
                                delete column.filterValues;
                                delete column.filterValue;
                                if (!this.keepSorting) {
                                    delete column.sortOrder;
                                    delete column.sortIndex;
                                }
                            }
                        localStorageService.SetItem(this.storageKey || 'storage', JSON.stringify(state));
                    },
                    customLoad() {
                        return JSON.parse(localStorageService.GetItem(this.storageKey || 'storage'));
                    }
                },

                columnChooser:
                {
                    enabled: true,
                    height: 400,
                    mode: 'select',
                    search: {
                        enabled: true
                    }
                },

                headerFilter:
                {
                    height: 400,
                    container: '.app-container',
                    search: {
                        enabled: true
                    },
                    visible: false
                },

                scrolling:
                {
                    mode: 'infinite',
                    preloadEnabled: true,
                    useNative: false
                },

                export:
                {
                    excelFilterEnabled: true,
                },

                remoteOperations:
                {
                    filtering: true,
                    paging: true,
                    sorting: true,
                },


                editing:
                {
                    useIcons: true,
                    selectTextOnEditStart: !Detector.isMobile(),

                    //Show as 2 columns even on small screens to make it more clear that is a row
                    form:
                    {
                        colCountByScreen:
                        {
                            xs: 2
                        }
                    }
                },

                searchPanel: {
                    width: '100%'
                },

                //Hide all items in the toolbar besides search (shown because of excel export, column chooser, add button)
                onToolbarPreparing: (e) => {
                    const { items } = e.toolbarOptions;
                    const newItems = items.filter(item => item.isCustom); // get custom items
                    const instance = e.component.instance();

                    if (instance.AllowCustomization) {
                        const widget = items.find(x => x.name === 'columnChooserButton');
                        widget.options.icon = 'm4u-icon m4u-icon-eye';
                        newItems.push({
                            ...widget,
                            location: 'after'
                        });
                    }

                    if (instance.option('searchPanel.visible')) {
                        const widget = items.find(x => x.name === 'searchPanel');
                        newItems.push({
                            ...widget,
                            location: 'center'
                        });
                    }

                    if (instance.option('groupPanel.visible')) {
                        instance.option('groupPanel.emptyPanelText', RC('lblGroupPanel'));
                        const widget = items.find(x => x.name === 'groupPanel');

                        //let groupingCount = 0;
                        //for (var i = 0; i < instance.columnCount(); i++) {
                        //    const groupIndex = instance.columnOption(i, "groupIndex");
                        //    if (groupIndex != undefined && groupIndex >= 0) {
                        //        groupingCount++;
                        //    }
                        //}
                        //if (groupingCount === 0) {
                        //    const tooltip = document.createElement('div');
                        //    tooltip.classList.add('group-panel-tooltip-container');

                        //    const tooltipIcon = document.createElement('i');
                        //    tooltipIcon.classList.add('group-panel-tooltip-icon');
                        //    tooltipIcon.classList.add('mdi');
                        //    tooltipIcon.classList.add('mdi-information-outline');

                        //    const tooltipText = document.createElement('span');
                        //    tooltipText.classList.add('group-panel-tooltip-text');
                        //    tooltipText.append(RC('groupPanelTooltipText'));

                        //    tooltipIcon.appendChild(tooltipText);
                        //    tooltip.appendChild(tooltipIcon);

                        //    newItems.push({
                        //        name: 'tooltip',
                        //        options: {
                        //            icon: 'mdi mdi-information-outline',
                        //            widget: 'dxButton'
                        //        },
                        //        template: () => tooltip,
                        //        isCustom: true,
                        //        location: 'before'
                        //    });
                        //}

                        newItems.push({
                            ...widget,
                            location: 'before'
                        });
                    }

                    if (!newItems.length) {
                        e.element.classList.add('m4u-hidegridtoolbar');
                    }

                    e.toolbarOptions.items = newItems;
                },

                onContentReady: (e) => {
                    const gridSummary = e.component.option('summary');
                    let rowsCount = e.component.totalCount();
                    rowsCount = rowsCount == -1 ? e.component.getVisibleRows().length : rowsCount;
                    if (gridSummary && gridSummary.totalItems
                        && gridSummary.totalItems.length && rowsCount > 0) e.element.classList.add('grid-has-summary');
                    const screen = AppInjector.get(ScreenService);
                    // this is official suggestion of devextreme support
                    const columnChooserView = e.component.getView('columnChooserView');
                    if (columnChooserView.element() && !columnChooserView._popupContainer) {
                        columnChooserView._initializePopupContainer();
                        columnChooserView.render();
                        const position = screen.IsRTL() //SysGlobals.Language == EnumLanguages.Hebrew
                            ? { of: e.element, at: 'left top', my: 'left top', offset: '0 50' }
                            : { of: e.element, my: 'right top', at: 'right top', offset: '0 50' };
                        columnChooserView._popupContainer.option('position', position);
                    }
                },

                onRowPrepared: (e) => {
                    if (e.rowType === 'group') {
                        if (e.groupIndex > 0) {
                            e.rowElement.classList.add('child-row');
                        }
                        if (e.isExpanded) e.rowElement.classList.add('row-expanded');
                    }
                },

                onRowExpanded: (e) => {
                    if (!e.key || (Array.isArray(e.key) && !e.key.every(i => i !== undefined))) return;

                    const getItemByKey = (items: Array<any>, key: Array<any>) => {
                        if (!(items && key)) {
                            return undefined;
                        }
                        let item: any = { items: items };
                        for (let i = 0; i < key.length && item; i++) {
                            item = item.items.find(itm => itm.key === key[i]);
                        }
                        return item;
                    };

                    const expandRecursively = (grid, item, parentKey) => {
                        if (!item) {
                            return;
                        }
                        grid.expandRow(parentKey);
                        const itemsCount = (item.items ? item.items.length : 0) + (item.collapsedItems ? item.collapsedItems.length : 0);

                        if (itemsCount === 1) {
                            if (item.items) {
                                expandRecursively(grid, item.items[0], [...parentKey, item.items[0].key]);
                            }
                            if (item.collapsedItems) {
                                expandRecursively(grid, item.collapsedItems[0], [...parentKey, item.collapsedItems[0].key]);
                            }
                        }
                    };

                    expandRecursively(e.component, getItemByKey(e.component.getDataSource()?.items(), e.key), e.key);
                },
                //Apply common properties on all columns
                customizeColumns(columns) {
                    const screen = AppInjector.get(ScreenService);

                    columns.forEach(oCol => {
                        // Customize sorting and filtering
                        if (oCol.allowSorting !== undefined) {
                            oCol.allowFiltering = oCol.allowSorting;
                        }

                        if (oCol.search === undefined || !oCol.search.enabled) {
                            oCol.search = {
                                enabled: true
                            };
                        }

                        //Take lookup columns
                        if (oCol.lookup != undefined) {

                            //Set default values
                            if (oCol.lookup.valueExpr == undefined) oCol.lookup.valueExpr = 'Value';
                            if (oCol.lookup.displayExpr == undefined) oCol.lookup.displayExpr = 'Text';

                            //Take only columns with defined datasource, and ignore those where datasource is an array of values (usually coming from LIST())
                            //Take columns that can be filtered
                            if (oCol.allowFiltering && oCol.lookup.dataSource != undefined && !(oCol.lookup.dataSource instanceof Array)) {

                                //Set the sorting for header filter from display expression
                                oCol.lookup.dataSource =
                                {
                                    store: oCol.lookup.dataSource,
                                    sort: oCol.lookup.displayExpr
                                };
                            }
                        }

                        //Init sub-settings for clearer code below
                        if (oCol.formItem == undefined) oCol.formItem = {};
                        if (oCol.editorOptions == undefined) oCol.editorOptions = {};


                        //Hidden columns should not be shown in column chooser either
                        //Use showInColumnChooser for "system" columns
                        //if (oCol.showInColumnChooser == false) oCol.visible = false;

                        //Hidden columns should not be shown in form mode either
                        oCol.formItem.visible = oCol.visible;
                        oCol.formItem.visibleIndex = oCol.visibleIndex;

                        //Set format on editors as well
                        if (oCol.format != undefined) {
                            //Set for normal editors
                            oCol.editorOptions.format = oCol.format;

                            //Set for form editing mode
                            oCol.formItem.format = oCol.format;
                        }

                        if (oCol.type === 'buttons') {
                            oCol.cssClass = `${oCol.cssClass || ''} fixed-column`;
                            oCol.minWidth = oCol.minWidth || 32;
                            oCol.allowResizing = false;
                            oCol.allowReordering = false;
                            oCol.allowGrouping = false;
                        }
                    });
                },

                onCellPrepared: (e) => {
                    //Add class to the boolean columns to change devexpress "center" alignment
                    if (e.column.dataType === 'boolean') { // && (e.rowType === 'header' || (e.rowType === 'data' && e.row.isNewRow))) {
                        e.cellElement.classList.add('dx-column-boolean-type');
                    }
                    if (e.column.dataType === 'number') {
                        e.cellElement.classList.add('dx-column-number-type');

                        const framework = AppInjector.get(FrameworkService);
                        const formats = [framework.CompanyInfo.NumberPrice, framework.CompanyInfo.NumberSum,
                        framework.CompanyInfo.NumberRate, framework.CompanyInfo.NumberPercent];
                        if (formats.indexOf(e.column.format) >= 0) {
                            e.cellElement.classList.add('dx-column-number-formatted');
                        }
                    }
                    //Add tooltip to the header
                    if (e.rowType === 'header' && e.column.caption) {
                        e.cellElement.title = e.column.caption;
                    }
                },

                onContextMenuPreparing: (e) => {
                    if (e.target == 'header') {
                        // e.items can be undefined
                        if (!e.items) e.items = [];
                        const grid = e.component;
                        const gridInstance = grid.instance();
                        const columns = grid.option('columns') as any[];
                        const isAllowCustomization = (gridInstance as any).AllowCustomization;
                        const isAllowAdvancedFilters = (gridInstance as any).AllowAdvancedFilters;

                        if (isAllowCustomization) {

                            // Add a Hide Column menu item
                            if (e.column.allowHiding) {
                                e.items.push({
                                    text: RC('lblHideColumn'),
                                    icon: ' mdi mdi-eye-off',
                                    onItemClick: () => {
                                        grid.columnOption(e.column.dataField, 'visible', false);
                                        grid.refresh();
                                    }
                                });
                            }

                            e.items.push({
                                text: RC('lblShowHiddenColumns'),
                                icon: ' mdi mdi-eye',
                                onItemClick: () => {
                                    columns.forEach(c => {
                                        if (!c.groupCellTemplate) grid.columnOption(c.dataField, 'visible', true);
                                    });
                                    grid.refresh();
                                }
                            });

                            e.items.push({
                                text: RC('lblColumnChooser'),
                                icon: ' mdi mdi-cog',
                                onItemClick: () => {
                                    gridInstance.option(
                                        {
                                            'columnChooser.mode': 'select',
                                            'columnChooser.search.enabled': true,
                                        });
                                    gridInstance.showColumnChooser();
                                }
                            });

                            e.items.push({
                                text: RC('lblRestoreDefaultView'),
                                icon: ' mdi mdi-cog-refresh',
                                onItemClick: () => {
                                    const storageKey = gridInstance.option('stateStoring.storageKey') || 'storage';
                                    localStorageService.RemoveItem(storageKey);
                                    gridInstance.state({});
                                }
                            });
                        }
                        const filterVisible = gridInstance.option('headerFilter.visible');
                        const { column } = e;

                        if (isAllowAdvancedFilters) {
                            e.items.push({
                                text: filterVisible ? RC('lblHideGridFilter') : RC('lblShowGridFilter'),
                                icon: ' mdi mdi-filter-variant',
                                onItemClick: () => {
                                    gridInstance.option(
                                        {
                                            headerFilter: {
                                                visible: !filterVisible,
                                                search: {
                                                    enabled: true
                                                }
                                            }
                                        });

                                    if (filterVisible) {
                                        gridInstance.clearFilter();
                                    } else {
                                        //show filter popup
                                        if (column.allowFiltering !== false) {
                                            gridInstance.getController('headerFilter').showHeaderFilterMenu(column.index);
                                        }
                                    }
                                }
                            });
                        }


                    }
                }

            }
        });
}
